import { Routes, Route } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { ProtectedRoute } from './components/auth/ProtectedRoute'

// Main pages - load immediately (no lazy) for instant navigation
import Home from './pages/Home/Home'
import About from './pages/About/About'
import Demos from './pages/Demos/DemoList'
import EventsCalendar from './pages/Events/EventsCalendar'

// Secondary pages - lazy load to reduce initial bundle
const DemoDetail = lazy(() => import('./pages/Demos/DemoDetail'))
const Booking = lazy(() => import('./pages/Booking/BookingCalendar'))
const BookingForm = lazy(() => import('./pages/Booking/BookingForm'))
const EventDetail = lazy(() => import('./pages/Events/EventDetail'))
const LabStatus = lazy(() => import('./pages/LabStatus/LabStatus'))
const PrivacyPolicy = lazy(() => import('./pages/Legal/PrivacyPolicy'))

// Admin pages
const AdminLogin = lazy(() => import('./pages/Admin/Login'))
const AdminDashboard = lazy(() => import('./pages/Admin/Dashboard'))
const AdminProducts = lazy(() => import('./pages/Admin/ProductManager'))
const AdminBookings = lazy(() => import('./pages/Admin/BookingManager'))
const AdminUsers = lazy(() => import('./pages/Admin/UserManager'))
const AdminProfile = lazy(() => import('./pages/Admin/AdminProfile'))
const UserProfile = lazy(() => import('./pages/Admin/UserProfile'))
const UserEdit = lazy(() => import('./pages/Admin/UserEdit'))
const AdminLabStatus = lazy(() => import('./pages/Admin/AdminLabStatus'))
const DemoEditor = lazy(() => import('./pages/Admin/DemoEditor'))
const ProductEditor = lazy(() => import('./pages/Admin/ProductEditor'))
const SystemSettings = lazy(() => import('./pages/Admin/SystemSettings'))
const DeviceManager = lazy(() => import('./pages/Admin/DeviceManager'))
const CentralAPITest = lazy(() => import('./pages/Admin/CentralAPITest'))

// 404 page
const NotFound = lazy(() => import('./pages/NotFound'))

export function AppRouter() {
  return (
    <Suspense fallback={<div className="min-h-screen animated-gradient-bg" />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/demos" element={<Demos />} />
        <Route path="/demos/:id" element={<DemoDetail />} />
        <Route path="/events" element={<EventsCalendar />} />
        <Route path="/events/:eventId" element={<EventDetail />} />
        <Route path="/booking" element={<Booking />} />
        <Route path="/booking/new/:demoId?" element={<BookingForm />} />
        <Route path="/lab-status" element={<LabStatus />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />

        {/* Admin Routes */}
        <Route path="/vadminx/start" element={<AdminLogin />} />
        <Route path="/admin" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/products" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminProducts />
          </ProtectedRoute>
        } />
        <Route path="/admin/products/new" element={
          <ProtectedRoute requireAdmin={true}>
            <ProductEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/devices" element={
          <ProtectedRoute requireAdmin={true}>
            <DeviceManager />
          </ProtectedRoute>
        } />
        <Route path="/admin/bookings" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminBookings />
          </ProtectedRoute>
        } />
        <Route path="/admin/users" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminUsers />
          </ProtectedRoute>
        } />
        <Route path="/admin/users/:id" element={
          <ProtectedRoute requireAdmin={true}>
            <UserProfile />
          </ProtectedRoute>
        } />
        <Route path="/admin/users/:id/edit" element={
          <ProtectedRoute requireAdmin={true}>
            <UserEdit />
          </ProtectedRoute>
        } />
        <Route path="/admin/profile" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminProfile />
          </ProtectedRoute>
        } />
        <Route path="/admin/lab-status" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLabStatus />
          </ProtectedRoute>
        } />
        <Route path="/admin/demos/new" element={
          <ProtectedRoute requireAdmin={true}>
            <DemoEditor />
          </ProtectedRoute>
        } />
        <Route path="/admin/settings" element={
          <ProtectedRoute requireAdmin={true}>
            <SystemSettings />
          </ProtectedRoute>
        } />
        <Route path="/admin/central-test" element={
          <ProtectedRoute requireAdmin={true}>
            <CentralAPITest />
          </ProtectedRoute>
        } />

        {/* 404 Route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  )
}